import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, ExternalLink, Globe, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OfficialPartnerBadge } from "@/components/official-partner-badge";
import { ShareProfileButton } from "@/components/share-profile-button";
import { fetchPartnerBySlug, PARTNER_CATEGORY_LABEL } from "@/lib/advertising";
import { supabase } from "@/integrations/supabase/client";
import { SITE_URL, socialImageMeta, breadcrumbLd } from "@/lib/seo";
import { SmartImage } from "@/components/smart-image";
import { useTranslation } from "@/i18n";


export const Route = createFileRoute("/partners/$slug")({
  head: ({ params }) => {
    const title = `${params.slug} — BidNova Partner`;
    const description = `Official partner profile on BidNova Auctions — verified UK trading card shops, dealers and brands.`;
    const url = `${SITE_URL}/partners/${params.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "profile" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...socialImageMeta(),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Partners", path: "/partners" },
          { name: params.slug, path: `/partners/${params.slug}` },
        ]),
      ],
    };
  },
  errorComponent: () => <PartnerErrorFallback />,
  notFoundComponent: () => <PartnerNotFoundFallback />,
  component: PartnerPage,
});

function PartnerErrorFallback() {
  const { t } = useTranslation("community");
  return <div className="p-10 text-center text-muted-foreground">{t("partners.detail.unavailable", "Partner unavailable.")}</div>;
}

function PartnerNotFoundFallback() {
  const { t } = useTranslation("community");
  return <div className="p-10 text-center text-muted-foreground">{t("partners.detail.notFound", "Partner not found.")}</div>;
}

function PartnerPage() {
  const { t } = useTranslation("community");
  const { slug } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ["partner", slug],
    queryFn: () => fetchPartnerBySlug(slug),
  });
  const linkedSellerId = data?.linked_profile_id ?? null;
  const { data: auctionCounts } = useQuery({
    queryKey: ["partner-auction-counts", linkedSellerId],
    enabled: !!linkedSellerId,
    staleTime: 60_000,
    queryFn: async () => {
      const [activeRes, endedRes] = await Promise.all([
        supabase
          .from("auctions")
          .select("id", { count: "exact", head: true })
          .eq("seller_id", linkedSellerId as string)
          .eq("status", "active"),
        supabase
          .from("auctions")
          .select("id", { count: "exact", head: true })
          .eq("seller_id", linkedSellerId as string)
          .in("status", ["ended", "sold"]),
      ]);
      return { active: activeRes.count ?? 0, sold: endedRes.count ?? 0 };
    },
  });

  if (isLoading) return <div className="p-16 text-center text-muted-foreground">{t("partners.detail.loading", "Loading…")}</div>;
  if (!data) throw notFound();

  const socials = (data.social_links ?? {}) as Record<string, string>;
  const socialEntries = Object.entries(socials).filter(([, v]) => typeof v === "string" && v);

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      {/* Cover */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-purple-950/40 via-black to-black">
        {data.cover_image_url && (
          <SmartImage src={data.cover_image_url} alt={`${data.business_name} cover image`} width={1280} height={256} widths={[640, 960, 1280, 1600]} sizes="100vw" className="h-48 w-full object-cover opacity-60 sm:h-64" />
        )}
        <div className="p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start">
          <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-gold/30 bg-black/60">
            {data.logo_url ? (
              <SmartImage src={data.logo_url} alt={`${data.business_name} logo`} width={96} height={96} widths={[96, 192]} resize="contain" className="h-full w-full object-contain p-2" />
            ) : (
              <span className="font-display text-2xl text-gold">{data.business_name.slice(0, 2).toUpperCase()}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-3xl text-white sm:text-4xl">{data.business_name}</h1>
              {data.is_verified && <BadgeCheck className="h-5 w-5 text-emerald-400" aria-label="Verified" />}
              {data.is_official_partner && <OfficialPartnerBadge />}
            </div>
            <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-gold">
              {PARTNER_CATEGORY_LABEL[data.partner_category]}
            </p>
            {data.description && <p className="mt-4 text-muted-foreground">{data.description}</p>}
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              {data.location && (<span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{data.location}</span>)}
              {data.website_url && (
                <a href={data.website_url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1.5 text-gold hover:underline">
                  <Globe className="h-4 w-4" /> {t("partners.detail.website", "Website")} <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {data.member_since && (<span>{t("partners.detail.partnerSince", "Partner since {year}", { year: new Date(data.member_since).getFullYear() })}</span>)}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <ShareProfileButton
                url={`https://bidnovaauctions.uk/partners/${data.slug}`}
                title={`${data.business_name} — BidNova Partner`}
                text={data.description ?? undefined}
              />
              {data.contact_email && (
                <Button asChild size="sm" variant="outline" className="h-8 gap-1.5 rounded-full border-border/60 text-xs">
                  <a href={`mailto:${data.contact_email}`} aria-label={`Contact ${data.business_name} by email`}>
                    <Mail className="h-3.5 w-3.5" aria-hidden /> {t("partners.detail.contact", "Contact")}
                  </a>
                </Button>
              )}
            </div>
            {linkedSellerId && auctionCounts && (
              <div className="mt-5 grid grid-cols-3 gap-3 max-w-md">
                <StatTile label={t("partners.detail.active", "Active")} value={auctionCounts.active} />
                <StatTile label={t("partners.detail.sold", "Sold")} value={auctionCounts.sold} />
                <StatTile label={t("partners.detail.since", "Since")} value={data.member_since ? String(new Date(data.member_since).getFullYear()) : "—"} />
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Body */}
      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {data.about && (
            <Card title={t("partners.detail.about", "About")}>
              <p className="whitespace-pre-line text-muted-foreground">{data.about}</p>
            </Card>
          )}
          {data.shipping_info && (
            <Card title={t("partners.detail.shipping", "Shipping & Delivery")}><p className="whitespace-pre-line text-muted-foreground">{data.shipping_info}</p></Card>
          )}
          {data.policies && (
            <Card title={t("partners.detail.policies", "Policies")}><p className="whitespace-pre-line text-muted-foreground">{data.policies}</p></Card>
          )}
        </div>

        <aside className="space-y-6">
          {data.linked_profile_id && (
            <Card title={t("partners.detail.bidnovaShop", "BidNova Shop")}>
              <p className="text-sm text-muted-foreground mb-3">{t("partners.detail.alsoSells", "This partner also sells on BidNova.")}</p>
              <Button asChild className="w-full bg-gold text-black hover:bg-gold/90">
                <Link to="/seller/$sellerId" params={{ sellerId: data.linked_profile_id }}>{t("partners.detail.visitShop", "Visit shop")}</Link>
              </Button>
            </Card>
          )}
          {socialEntries.length > 0 && (
            <Card title={t("partners.detail.social", "Social")}>
              <ul className="space-y-1.5 text-sm">
                {socialEntries.map(([k, v]) => (
                  <li key={k}>
                    <a href={v} target="_blank" rel="noopener noreferrer nofollow" className="text-gold hover:underline capitalize">{k}</a>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {data.contact_email && (
            <Card title={t("partners.detail.contact", "Contact")}>
              <a href={`mailto:${data.contact_email}`} className="text-sm text-gold hover:underline break-all">{data.contact_email}</a>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-card/40 p-6">
      <h2 className="mb-3 font-display text-xl text-white">{title}</h2>
      {children}
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-center">
      <div className="font-display text-xl text-gold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}
